import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Check, CheckCheck, X, Calendar, Dumbbell, AlertTriangle, MessageSquare, Zap, Clock, Droplets, Trash2, ChevronRight, Crown, Shield, Users } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { playMilitaryNotifSound } from '../utils/notificationSound';

const NOTIFICATION_ICONS = {
  plan_expiry_client: Calendar,
  plan_expiry_coach: Calendar,
  plan_deployed: Dumbbell,
  meal_reminder: Zap,
  streak_warning: AlertTriangle,
  challenge_result: Zap,
  coach_message: MessageSquare,
  hydration_reminder: Droplets,
  team_assignment: Shield,
  monthly_mvp: Crown,
  monthly_mvp_announce: Crown,
  monthly_team_reassign: Users,
  system: Bell,
};

const NOTIFICATION_COLORS = {
  plan_expiry_client: 'text-amber-400',
  plan_expiry_coach: 'text-amber-400',
  plan_deployed: 'text-green-400',
  meal_reminder: 'text-primary',
  streak_warning: 'text-red-400',
  challenge_result: 'text-blue-400',
  coach_message: 'text-primary',
  hydration_reminder: 'text-blue-400',
  team_assignment: 'text-primary',
  monthly_mvp: 'text-yellow-400',
  monthly_mvp_announce: 'text-yellow-400',
  monthly_team_reassign: 'text-blue-400',
  system: 'text-muted-foreground',
};

function timeAgo(dateStr, fr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return fr ? "a l'instant" : 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}${fr ? 'j' : 'd'}`;
  return date.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const hasInteracted = useRef(false);

  // Track user interaction for audio autoplay policy
  useEffect(() => {
    const handler = () => { hasInteracted.current = true; };
    window.addEventListener('click', handler, { once: true });
    window.addEventListener('touchstart', handler, { once: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/unread-count');
      const newCount = res.data.unread_count || 0;
      // Play sound when new notifications arrive
      if (newCount > prevUnreadRef.current && prevUnreadRef.current >= 0 && hasInteracted.current) {
        try {
          playMilitaryNotifSound();
        } catch (soundErr) {
          // Silent fail — audio device unavailable, don't crash the app
          console.warn('[Notifications] Audio playback failed:', soundErr);
        }
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch { /* */ }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/notifications/inbox');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [user]);

  // Polling every 30s for unread count (quasi real-time)
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markRead = async (notifId) => {
    try {
      await api.put(`/notifications/read/${notifId}`);
      setNotifications(prev => prev.map(n =>
        n.notification_id === notifId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* */ }
  };

  // Iter. 411.mm14 — Type-based fallback link map so notifications that
  // predate the addition of an explicit `link` field on the backend
  // (e.g. "team_assignment" for Clash des Meutes, "badge_unlocked" for the
  // Vestiaire) still route the user somewhere useful when tapped. Before
  // this map, tapping "🔴 Bienvenue dans l'Équipe Rouge !" in the
  // Messagerie overlay did nothing.
  const TYPE_TO_LINK = React.useMemo(() => ({
    team_assignment: '/clash',
    clash_reassignment: '/clash',
    clash_result: '/clash',
    badge_unlocked: '/vestiaire',
    weekly_quest: '/dashboard',
    pack_challenge: '/clash',
    leaderboard_rank_up: '/clash',
    streak_milestone: '/vestiaire',
    monthly_report: '/monthly-report',
    friday_debrief: '/friday-debrief',
    coach_message: '/messaging',
  }), []);

  const handleNotifClick = useCallback((notif) => {
    // Mark as read
    if (!notif.read) {
      markRead(notif.notification_id);
    }

    // Determine navigation link (explicit link > client shortcut > type fallback)
    const link = notif.link
      || notif.data?.link
      || (notif.data?.client_id ? `/coach?client=${notif.data.client_id}` : null)
      || (notif.data?.url ? notif.data.url : null)
      || TYPE_TO_LINK[notif.type]
      || null;

    if (link) {
      setOpen(false);
      navigate(link);
    }
  }, [navigate, TYPE_TO_LINK]);

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button — GLOW aesthetic (Iter. 411.u) */}
      <button
        onClick={() => setOpen(!open)}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 ${
          unreadCount > 0 ? 'text-amber-100' : 'text-orange-200/80 hover:text-orange-100'
        }`}
        style={{
          background: unreadCount > 0
            ? 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(245,158,11,0.22) 100%)'
            : 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(15,15,15,0.5) 45%, rgba(251,146,60,0.12) 100%)',
          border: unreadCount > 0
            ? '1px solid rgba(245,158,11,0.55)'
            : '1px solid rgba(251,146,60,0.35)',
          boxShadow: unreadCount > 0
            ? '0 0 18px rgba(245,158,11,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 0 12px rgba(251,146,60,0.28), inset 0 1px 0 rgba(255,255,255,0.05)',
          backdropFilter: 'blur(6px)',
        }}
        data-testid="notification-bell"
      >
        <Bell
          className="w-5 h-5 relative"
          style={{ filter: unreadCount > 0 ? 'drop-shadow(0 0 6px rgba(245,158,11,0.9))' : 'drop-shadow(0 0 4px rgba(251,146,60,0.6))' }}
        />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[9px] font-black rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 animate-in zoom-in-50 text-white"
            style={{
              background: 'linear-gradient(140deg, #ef4444 0%, #b91c1c 100%)',
              boxShadow: '0 0 10px rgba(239,68,68,0.85), 0 0 4px rgba(239,68,68,0.5)',
              border: '1px solid rgba(239,68,68,0.9)',
            }}
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Mobile backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-[9998]"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Notification Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-20 left-3 right-3 md:absolute md:bottom-auto md:left-0 md:right-auto md:top-12 md:w-96 max-h-[60vh] md:max-h-[70vh] bg-card border border-border rounded-xl shadow-2xl z-[9999] overflow-hidden flex flex-col"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/30">
              <h3 className="font-heading text-sm uppercase tracking-wider text-foreground">
                {fr ? 'Notifications' : 'Notifications'}
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] text-primary hover:text-primary/80 font-medium uppercase tracking-wide"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3.5 h-3.5 inline mr-1" />
                    {fr ? 'Tout lire' : 'Read all'}
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {loading && notifications.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <BellOff className="w-8 h-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {fr ? 'Aucune notification' : 'No notifications'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {notifications.map((notif) => {
                    const IconComp = NOTIFICATION_ICONS[notif.type] || Bell;
                    const iconColor = NOTIFICATION_COLORS[notif.type] || 'text-muted-foreground';
                    const isRead = notif.read;
                    const hasLink = !!(notif.link || notif.data?.link || notif.data?.client_id || notif.data?.url || TYPE_TO_LINK[notif.type]);
                    return (
                      <div
                        key={notif.notification_id}
                        className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-secondary/30 ${
                          !isRead ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => handleNotifClick(notif)}
                        data-testid={`notif-item-${notif.notification_id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          !isRead ? 'bg-primary/15' : 'bg-secondary/50'
                        }`}>
                          <IconComp className={`w-4 h-4 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-xs font-semibold truncate ${
                              !isRead ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                              {notif.title}
                            </p>
                            {!isRead && (
                              <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-2">
                            {notif.body || notif.message}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Clock className="w-2.5 h-2.5 text-muted-foreground/40" />
                            <span className="text-[9px] text-muted-foreground/40">
                              {timeAgo(notif.created_at, fr)}
                            </span>
                          </div>
                        </div>
                        {hasLink && (
                          <ChevronRight className="w-4 h-4 text-muted-foreground/30 shrink-0 self-center" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
